import { toInclusiveDateRange } from '@dealstack/frontend/app/pages/Project/MIP/utils/transformDocumentFieldFilter';
import { NO_SIGNATURE_REQUIRED_FILTER_VALUE } from '@dealstack/frontend/shared/components/Table/MantineTable/Filters/InvestorFilter';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import {
	DisplayWorkflowStatus,
	mapToDisplayStatus,
	type PostDatingState,
} from '@dealstack/shared/lib/common/business/documentWorkflowStatus';
import {
	isConcludedSignatureStatus,
	isSettledSignatureStatus,
	isSignedSignatureStatus,
} from '@dealstack/shared/lib/common/business/signatureStatus';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import {
	DocumentSigningScope,
	DocumentWorkflowInstanceSignatureStatus,
	DocumentWorkflowInstanceStatus,
	InvestmentProgrammeDocumentDetailsRes,
	SignatureType,
} from '@dealstack/shared/lib/generated';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import type { InvestmentProgrammeDocumentsDataOperations } from './Documents.utils.types';
import type { DocumentsNavigationState } from './Documents.types';
import {
	emptyToUndefined,
	toIdListFilterValue,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils.ts';

export { DisplayWorkflowStatus, mapToDisplayStatus, type PostDatingState };

/** Brand green used for signed / completed signature statuses. */
export const SIGNED_STATUS_COLOR = defaultTheme.v3.colors.teal[8];

/**
 * Status colours for surfaces outside the signers panel. Every status stays
 * visually distinct so actionable ones don't read like terminal ones.
 */
export const PersonStatusColorMap: Record<
	DocumentWorkflowInstanceSignatureStatus | 'NO_SIGNATURE_REQUIRED',
	string
> = {
	[DocumentWorkflowInstanceSignatureStatus.CREATED]: defaultTheme.v3.colors.orange[6],
	[DocumentWorkflowInstanceSignatureStatus.PENDING_SIGNATURE]: defaultTheme.v3.colors.blue[6],
	[DocumentWorkflowInstanceSignatureStatus.SIGNED]: SIGNED_STATUS_COLOR,
	[DocumentWorkflowInstanceSignatureStatus.ERROR]: defaultTheme.v3.colors.red[6],
	[DocumentWorkflowInstanceSignatureStatus.DECLINED]: defaultTheme.v3.colors.gray[8],
	[DocumentWorkflowInstanceSignatureStatus.EXPIRED]: defaultTheme.v3.colors.gray[8],
	[DocumentWorkflowInstanceSignatureStatus.WAITING_FOR_WITNESS_SIGNATURES]:
		defaultTheme.v3.colors.orange[6],
	[DocumentWorkflowInstanceSignatureStatus.COMPLETED]: SIGNED_STATUS_COLOR,
	[DocumentWorkflowInstanceSignatureStatus.CLOSED_WITHOUT_SIGNATURE]:
		defaultTheme.v3.colors.gray[8],
	NO_SIGNATURE_REQUIRED: defaultTheme.v3.colors.gray[8],
};

/**
 * Neutralised status colours for the signers panel pills: brand green for
 * signed/completed, red for errors, black for in-progress statuses, gray for
 * terminal ones (declined/expired).
 */
export const SignerPanelStatusColorMap: Record<
	DocumentWorkflowInstanceSignatureStatus | 'NO_SIGNATURE_REQUIRED',
	string
> = {
	...PersonStatusColorMap,
	[DocumentWorkflowInstanceSignatureStatus.CREATED]: defaultTheme.v3.colors.black[0],
	[DocumentWorkflowInstanceSignatureStatus.PENDING_SIGNATURE]: defaultTheme.v3.colors.black[0],
	[DocumentWorkflowInstanceSignatureStatus.WAITING_FOR_WITNESS_SIGNATURES]:
		defaultTheme.v3.colors.black[0],
};

export const DisplayWorkflowStatusColorMap: Record<DisplayWorkflowStatus, string> = {
	[DisplayWorkflowStatus.COMPLETED]: defaultTheme.v3.colors.teal[8],
	[DisplayWorkflowStatus.IN_PROGRESS]: defaultTheme.v3.colors.orange[6],
	[DisplayWorkflowStatus.AWAITING_DATE]: defaultTheme.v3.colors.blue[6],
	[DisplayWorkflowStatus.REVOKED]: defaultTheme.v3.colors.gray[8],
	[DisplayWorkflowStatus.ERROR]: defaultTheme.v3.colors.red[6],
};

export const WorkflowStatusColorMap: Record<DocumentWorkflowInstanceStatus, string> = {
	[DocumentWorkflowInstanceStatus.PENDING]: DisplayWorkflowStatusColorMap.IN_PROGRESS,
	[DocumentWorkflowInstanceStatus.IN_PROGRESS]: DisplayWorkflowStatusColorMap.IN_PROGRESS,
	[DocumentWorkflowInstanceStatus.COMPLETED]: DisplayWorkflowStatusColorMap.COMPLETED,
	[DocumentWorkflowInstanceStatus.CANCELLED]: DisplayWorkflowStatusColorMap.REVOKED,
	[DocumentWorkflowInstanceStatus.ERROR]: DisplayWorkflowStatusColorMap.ERROR,
	[DocumentWorkflowInstanceStatus.EXPIRED]: DisplayWorkflowStatusColorMap.ERROR,
	[DocumentWorkflowInstanceStatus.PENDING_ADDITIONAL_SIGNATURES]:
		DisplayWorkflowStatusColorMap.IN_PROGRESS,
	[DocumentWorkflowInstanceStatus.PENDING_FORM_SUBMISSION]:
		DisplayWorkflowStatusColorMap.IN_PROGRESS,
	[DocumentWorkflowInstanceStatus.REJECTED]: DisplayWorkflowStatusColorMap.IN_PROGRESS,
};

export const getDisplayWorkflowStatusText = (
	displayStatus: DisplayWorkflowStatus,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
): string => {
	const statusTextMap: Record<DisplayWorkflowStatus, string> = {
		[DisplayWorkflowStatus.COMPLETED]: tMipManage('documents_page.workflow_status.completed'),
		[DisplayWorkflowStatus.IN_PROGRESS]: tMipManage(
			'documents_page.workflow_status.in_progress'
		),
		[DisplayWorkflowStatus.AWAITING_DATE]: tMipManage(
			'documents_page.workflow_status.awaiting_date'
		),
		[DisplayWorkflowStatus.REVOKED]: tMipManage('documents_page.workflow_status.revoked'),
		[DisplayWorkflowStatus.ERROR]: tMipManage('documents_page.workflow_status.error'),
	};
	return statusTextMap[displayStatus];
};

export const getWorkflowStatusText = (
	status: DocumentWorkflowInstanceStatus,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>,
	postDating?: PostDatingState | null
): string => getDisplayWorkflowStatusText(mapToDisplayStatus(status, postDating), tMipManage);

export const getPersonStatusText = (
	status: DocumentWorkflowInstanceSignatureStatus | null | undefined,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
): string => {
	if (status == null) {
		return tMipManage('documents_page.person_status.no_signature_required');
	}
	const statusTextMap: Record<DocumentWorkflowInstanceSignatureStatus, string> = {
		[DocumentWorkflowInstanceSignatureStatus.CREATED]: tMipManage(
			'documents_page.person_status.created'
		),
		[DocumentWorkflowInstanceSignatureStatus.SIGNED]: tMipManage(
			'documents_page.person_status.signed'
		),
		[DocumentWorkflowInstanceSignatureStatus.PENDING_SIGNATURE]: tMipManage(
			'documents_page.person_status.pending_signature'
		),
		[DocumentWorkflowInstanceSignatureStatus.DECLINED]: tMipManage(
			'documents_page.person_status.declined'
		),
		[DocumentWorkflowInstanceSignatureStatus.EXPIRED]: tMipManage(
			'documents_page.person_status.expired'
		),
		[DocumentWorkflowInstanceSignatureStatus.ERROR]: tMipManage(
			'documents_page.person_status.error'
		),
		[DocumentWorkflowInstanceSignatureStatus.WAITING_FOR_WITNESS_SIGNATURES]: tMipManage(
			'documents_page.person_status.awaiting_for_witness_signatures'
		),
		[DocumentWorkflowInstanceSignatureStatus.COMPLETED]: tMipManage(
			'documents_page.person_status.completed'
		),
		[DocumentWorkflowInstanceSignatureStatus.CLOSED_WITHOUT_SIGNATURE]: tMipManage(
			'documents_page.person_status.closed_without_signature'
		),
	};
	return statusTextMap[status];
};

export const isSignedStatus = (status?: DocumentWorkflowInstanceSignatureStatus): boolean => {
	return isSignedSignatureStatus(status);
};

/**
 * Nothing left for this slot's signer to do — signed, or closed without a signature by an
 * admin (DC-1098). Gate actions on this; `isSignedStatus` answers the different question of
 * whether the signer actually signed, which progress counts and status chips still need.
 */
export const isConcludedStatus = (status?: DocumentWorkflowInstanceSignatureStatus): boolean => {
	return isConcludedSignatureStatus(status);
};

/** Signed with nothing outstanding — unlike `isSignedStatus`, excludes waiting-for-witness. */
export const isSettledStatus = (status?: DocumentWorkflowInstanceSignatureStatus): boolean => {
	return isSettledSignatureStatus(status);
};

export const DISPLAY_SIGNATURE_STATUS_TO_DB_STATUSES: Partial<
	Record<
		DocumentWorkflowInstanceSignatureStatus,
		readonly DocumentWorkflowInstanceSignatureStatus[]
	>
> = {
	[DocumentWorkflowInstanceSignatureStatus.CREATED]: [
		DocumentWorkflowInstanceSignatureStatus.CREATED,
		DocumentWorkflowInstanceSignatureStatus.PENDING_SIGNATURE,
	],
	[DocumentWorkflowInstanceSignatureStatus.SIGNED]: [
		DocumentWorkflowInstanceSignatureStatus.SIGNED,
		DocumentWorkflowInstanceSignatureStatus.WAITING_FOR_WITNESS_SIGNATURES,
		DocumentWorkflowInstanceSignatureStatus.COMPLETED,
	],
};

export const expandSignatureStatuses = (
	statuses: DocumentWorkflowInstanceSignatureStatus[] | undefined
): DocumentWorkflowInstanceSignatureStatus[] | undefined => {
	return statuses?.flatMap((s) => DISPLAY_SIGNATURE_STATUS_TO_DB_STATUSES[s] ?? [s]);
};

export enum CounterpartyOptionGroupKey {
	SIGNATURE_STATUS = 'signature-status',
	DOCUMENT_ROLE = 'document-role',
}

export const getSignatureStatusOptions = (
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
): Array<{
	value: string;
	label: string;
	groupKey: CounterpartyOptionGroupKey.SIGNATURE_STATUS;
	groupLabel: string;
}> => {
	const statuses = [
		{
			value: DocumentWorkflowInstanceSignatureStatus.CREATED,
			label: tMipManage('documents_page.signature_status.pending'),
		},
		{
			value: DocumentWorkflowInstanceSignatureStatus.SIGNED,
			label: tMipManage('documents_page.signature_status.signed'),
		},
		{
			value: DocumentWorkflowInstanceSignatureStatus.DECLINED,
			label: tMipManage('documents_page.signature_status.declined'),
		},
		{
			value: DocumentWorkflowInstanceSignatureStatus.EXPIRED,
			label: tMipManage('documents_page.signature_status.expired'),
		},
		{
			value: DocumentWorkflowInstanceSignatureStatus.ERROR,
			label: tMipManage('documents_page.signature_status.error'),
		},
	];

	return statuses.map((status) => ({
		value: `status:${status.value}`,
		label: status.label,
		groupKey: CounterpartyOptionGroupKey.SIGNATURE_STATUS,
		groupLabel: tMipManage('documents_page.signature_status.filter_group'),
	}));
};

export enum EligibleDocumentFilterMode {
	REVOKE = 'revoke',
	REISSUE = 'reissue',
	SIGN = 'sign',
	ASSIGN_COUNTERPARTY = 'assign-counterparty',
	ADD_COUNTERPARTY = 'add-counterparty',
	CLAIM_ON_BEHALF = 'claim-on-behalf',
	INVITE_AUTHORISED_SIGNATORY = 'invite-authorised-signatory',
	REMIND = 'remind',
	APPLY_DATE = 'apply-date',
}

export const getFileId = ({
	documentDetails,
	currentUserId,
}: {
	documentDetails: InvestmentProgrammeDocumentDetailsRes;
	currentUserId: string;
}): string => {
	const { signingScope, signatureType, fileId: instanceFileId, groups } = documentDetails;

	// Concluded, not signed: a document closed without a signature has already been merged
	// into the instance file, so its closed slots must not hold the viewer on their own page.
	const allParticipantsAreConcluded = groups.every((group) =>
		group.signers.every((signer) => isConcludedStatus(signer.status))
	);

	if (
		allParticipantsAreConcluded ||
		signingScope !== DocumentSigningScope.ALL_PARTICIPANTS ||
		signatureType !== SignatureType.WET_INK
	) {
		return instanceFileId;
	}

	const userSignatureFileIds: string[] = [];
	for (const group of groups) {
		for (const signer of group.signers) {
			if (signer.assigned && signer.userId === currentUserId && signer.fileId) {
				userSignatureFileIds.push(signer.fileId);
			}
		}
	}

	const latestUserFileId = userSignatureFileIds[userSignatureFileIds.length - 1];
	if (latestUserFileId) {
		return latestUserFileId;
	}

	return instanceFileId;
};

export const buildDocumentFilters = (
	dataOperations: InvestmentProgrammeDocumentsDataOperations
) => {
	const investorSignatureStatuses = expandSignatureStatuses(
		dataOperations.filter?.investor?.signatureStatuses?.filter(
			(s): s is DocumentWorkflowInstanceSignatureStatus =>
				s !== NO_SIGNATURE_REQUIRED_FILTER_VALUE
		)
	);

	return {
		quickFilters: dataOperations.quickFilters,
		fieldFilter: {
			documentName: dataOperations.filter?.documentName,
			investorName: dataOperations.filter?.investor?.investorName,
			investmentWorkflowId: dataOperations.filter?.investmentWorkflowId,
			investorIds: emptyToUndefined(toIdListFilterValue(dataOperations.filter?.investorId)),
			counterpartyName: dataOperations.filter?.counterparty?.counterpartyName,
			investorSignatureStatuses,
			counterparty: {
				roleNames: dataOperations.filter?.counterparty?.roleNames,
				signatureStatuses: expandSignatureStatuses(
					dataOperations.filter?.counterparty?.signatureStatuses
				),
			},
			displayStatuses: dataOperations.filter?.status,
			createdAt: toInclusiveDateRange(dataOperations.filter?.createdAt),
			updatedAt: toInclusiveDateRange(dataOperations.filter?.updatedAt),
			dealClassifierIds: dataOperations.filter?.dealClassifierIds,
		},
	};
};

const isEligibleDocumentFilterMode = (value: unknown): value is EligibleDocumentFilterMode =>
	Object.values(EligibleDocumentFilterMode).some((mode) => mode === value);

/**
 * Narrow `useLocation().state`, which react-router types as `any`, to the intent the documents
 * list accepts. Anything unrecognised is ignored rather than trusted.
 */
export const readDocumentsNavigationState = (state: unknown): DocumentsNavigationState => {
	if (
		typeof state !== 'object' ||
		state === null ||
		!('enterEligibleDocumentFilterMode' in state)
	) {
		return {};
	}
	const mode = state.enterEligibleDocumentFilterMode;
	return isEligibleDocumentFilterMode(mode) ? { enterEligibleDocumentFilterMode: mode } : {};
};
